import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { Damage } from "../../services/damage/Damage";
import { mainCodeColorStorage } from "../../services/mc-colors/MainCodeColorStorage";

interface PipeDiagramProps {
  damages: Damage[];
  endX?: number;
}

const PipeDiagram: React.FC<PipeDiagramProps> = ({ damages, endX}) => {
  const ref = useRef<SVGSVGElement | null>(null);
  
  const pipeTopPadding = 20;
  const pipeHeight = 800;
  const totalHeight = pipeTopPadding * 2 + pipeHeight;
  
  const stationValues = damages.map((damage) => ({
    stationValue: damage.displayValues.BM101?.value || '',
    coloredValue: damage.displayValues.BM105?.value
  }));  

  const arrowColors = mainCodeColorStorage.colors;

  const sortedData = [...stationValues].sort((a, b) => {
    const numA = parseFloat(a.stationValue.toString().replace(',', '.'));
    const numB = parseFloat(b.stationValue.toString().replace(',', '.'));
    return numA - numB;
  });
  
  const getRowCenterY = (damageId: number): number => {
    const row = document.querySelector(`[data-id='${damageId}']`);
    if (row) {
      const rect = row.getBoundingClientRect();  
      const svgRect = ref.current?.getBoundingClientRect();
      if (!svgRect) return 0;
      return rect.top - svgRect.top + rect.height / 2;
    }
      return 0;
    }

    useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const pipeX = 50;
    const pipeWidth = 20;
    const circleRadius = 15;  

    svg
      .append("rect")
      .attr("x", pipeX - pipeWidth / 2)
      .attr("y", pipeTopPadding)
      .attr("width", pipeWidth)
      .attr("height", pipeHeight)
      .attr("fill", "#88aacc")
      .attr("rx", 10);

    svg
      .append("circle")
      .attr("cx", pipeX)
      .attr("cy", pipeTopPadding)
      .attr("r", circleRadius)
      .attr("fill", "#88aacc");

    svg
      .append("circle")
      .attr("cx", pipeX)
      .attr("cy", pipeTopPadding + pipeHeight)
      .attr("r", circleRadius)
      .attr("fill", "#88aacc");

      const startX = pipeX + pipeWidth / 2
      const bendX = startX + 10;
      const minY = pipeTopPadding + circleRadius; 
      const maxY = pipeTopPadding + pipeHeight - circleRadius;

      const minStationValue = Math.min(...sortedData.map(item => {
        const num = parseFloat(item.stationValue.toString().replace(',', '.'));
        return isNaN(num) ? 0 : num;
      }));
      const maxStationValue = Math.max(...sortedData.map(item => {
        const num = parseFloat(item.stationValue.toString().replace(',', '.'));
        return isNaN(num) ? 0 : num;
      }));
      
      const yScale = d3.scaleLinear()
        .domain([minStationValue, maxStationValue])
        .range([minY, maxY]);

      setTimeout(() => {
      sortedData.forEach((item, arrayIndex) => {
        const stationNum = parseFloat(item.stationValue.toString().replace(',', '.'));
        const yStart = isNaN(stationNum) ? minY : yScale(stationNum);
        const yEnd = getRowCenterY(damages[arrayIndex].id);

        const color = arrowColors.find(c => c.mainCode === item.coloredValue)?.color || "gray";

        const arrow = `arrow-${damages[arrayIndex].id}`;
    
        svg
          .append("defs")
          .append("marker")
          .attr("id", arrow)
          .attr("viewBox", "0 0 10 10")
          .attr("refX", 10)
          .attr("refY", 5)
          .attr("markerWidth", 6)
          .attr("markerHeight", 6)
          .attr("orient", "auto-start-reverse")
          .append("path")
          .attr("d", "M 0 0 L 10 5 L 0 10 z")
          .attr("fill", color);

        svg
        .append("line")
        .attr("x1", startX)
        .attr("y1", yStart)
        .attr("x2", bendX)
        .attr("y2", yStart)
        .attr("stroke", color)
        .attr("stroke-width", 1.5);

        svg
        .append("line")
        .attr("x1", bendX)
        .attr("y1", yStart)
        .attr("x2", endX!)
        .attr("y2", yEnd)
        .attr("stroke", color)
        .attr("stroke-width", 1.5)
        .attr("marker-end", `url(#${arrow})`)
        .attr("data-damage-id", damages[arrayIndex].id);
      });

  }, 50);
}, [damages]);

  return <svg ref={ref} width={150} height={totalHeight} />;
};

export default PipeDiagram; 